import React, { useState, useEffect, useCallback } from 'react';
import { fetchEnterpriseJobApplications, reviewJobApplication } from '@/service/api/resume';
import JobApplicationCard from './JobApplicationCard';
import ResumeSnapshotModal, { ResumeSnapshotData } from './ResumeSnapshotModal';

export interface JobApplicationsModalProps {
  job: Api.Job.JobResponse | null;
  onClose: () => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

export default function JobApplicationsModal({
  job,
  onClose,
  showToast,
}: JobApplicationsModalProps) {
  const [applications, setApplications] = useState<Api.Resume.ApplicationResponse[]>([]);
  const [isAppsLoading, setIsAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState('');
  const [appsPage, setAppsPage] = useState(1);
  const [appsTotal, setAppsTotal] = useState(0);
  const appsPageSize = 10;
  const [reviewingAppId, setReviewingAppId] = useState<string | null>(null);
  const [appFilterStatus, setAppFilterStatus] = useState<string>('all');

  const [selectedSnapshot, setSelectedSnapshot] = useState<ResumeSnapshotData | null>(null);

  // 正在进行录用确认的申请标识与状态
  const [acceptConfirmApp, setAcceptConfirmApp] = useState<{ id: string } | null>(null);
  const [rejectOtherApplications, setRejectOtherApplications] = useState(false);

  const loadApplications = useCallback(async (jobId: string, page = 1) => {
    setIsAppsLoading(true);
    setAppsError('');
    try {
      const res = await fetchEnterpriseJobApplications(jobId, { page, page_size: appsPageSize });
      setApplications(res?.items || []);
      setAppsTotal(res?.total || 0);
      setAppsPage(page);
    } catch (err: any) {
      console.error('加载岗位申请列表失败:', err);
      setAppsError(err?.message || '加载申请列表失败，请稍后重试');
    } finally {
      setIsAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (job?.id) {
      setAppFilterStatus('all');
      loadApplications(job.id, 1);
    }
  }, [job?.id, loadApplications]);

  if (!job) return null;

  const handleReviewApp = async (appId: string, accepted: boolean, rejectOthers?: boolean) => {
    if (accepted && rejectOthers === undefined) {
      // 录用时弹出确认弹窗
      setAcceptConfirmApp({ id: appId });
      setRejectOtherApplications(false);
      return;
    }

    setReviewingAppId(appId);
    try {
      await reviewJobApplication(appId, {
        accepted,
        reject_other_applications: accepted ? Boolean(rejectOthers) : false,
      });
      showToast(
        accepted
          ? rejectOthers
            ? '已成功录用，并已拒绝其他申请完成招聘'
            : '已成功录用该求职者'
          : '已标记为未录用',
        'success'
      );
      if (selectedSnapshot && selectedSnapshot.appId === appId) {
        setSelectedSnapshot((prev) =>
          prev ? { ...prev, appStatus: accepted ? 'SELECTED' : 'REJECTED' } : null
        );
      }
      setAcceptConfirmApp(null);
      loadApplications(job.id, appsPage);
    } catch (error: any) {
      console.error('审核处理失败:', error);
      showToast(error?.message || '审核处理失败', 'error');
    } finally {
      setReviewingAppId(null);
    }
  };

  const handleViewSnapshot = (
    appId: string,
    appStatus: string,
    applicantName: string,
    snapshot: Api.Resume.ResumeResponse
  ) => {
    setSelectedSnapshot({
      appId,
      appStatus,
      applicantName,
      snapshot,
    });
  };

  const filteredApplications = applications.filter((app) => {
    const s = (app.status || '').toUpperCase();
    if (appFilterStatus === 'all') return true;
    if (appFilterStatus === 'pending') return s === 'PENDING' || s === 'PENDING_REVIEW' || s === 'SUBMITTED';
    if (appFilterStatus === 'accepted') return s === 'SELECTED' || s === 'ACCEPTED' || s === 'APPROVED' || s === 'PASSED';
    if (appFilterStatus === 'rejected') return s === 'REJECTED' || s === 'REFUSED';
    if (appFilterStatus === 'withdrawn') return s === 'WITHDRAWN';
    return true;
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
        <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
          {/* Modal 头部 */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <i className="ri-file-user-line text-primary-600 text-lg"></i>
                【{job.title}】的申请列表
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                累计收到 <span className="font-semibold text-primary-600">{appsTotal}</span> 份求职投递
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* 状态筛选 Tabs */}
          <div className="flex items-center gap-1.5 px-6 py-2.5 border-b border-slate-100 bg-slate-50/50 flex-wrap">
            {[
              { key: 'all', label: '全部' },
              { key: 'pending', label: '待审核' },
              { key: 'accepted', label: '已录用' },
              { key: 'rejected', label: '未录用' },
              { key: 'withdrawn', label: '已撤回' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setAppFilterStatus(tab.key)}
                className={`text-xs px-3 py-1 rounded-lg transition-all cursor-pointer font-medium ${
                  appFilterStatus === tab.key
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Modal 内容 */}
          <div className="p-6 overflow-y-auto flex-1 space-y-4">
            {isAppsLoading ? (
              <div className="py-16 text-center">
                <div className="w-8 h-8 mx-auto mb-3 rounded-full border-2 border-primary-200 border-t-primary-600 animate-spin"></div>
                <p className="text-xs text-slate-400">正在获取申请列表记录...</p>
              </div>
            ) : appsError ? (
              <div className="py-12 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xl text-red-500"></i>
                </div>
                <p className="text-xs text-red-500 mb-3">{appsError}</p>
                <button
                  type="button"
                  onClick={() => loadApplications(job.id, appsPage)}
                  className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-medium transition-all cursor-pointer"
                >
                  重试
                </button>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-slate-400"></i>
                </div>
                <p className="text-sm font-medium text-slate-700">暂无匹配的求职投递申请</p>
                <p className="text-xs text-slate-400 mt-1">请尝试选择其他筛选类型</p>
              </div>
            ) : (
              filteredApplications.map((app) => (
                <JobApplicationCard
                  key={app.id}
                  app={app}
                  jobStatus={job.status}
                  reviewingAppId={reviewingAppId}
                  onReview={handleReviewApp}
                  onViewSnapshot={handleViewSnapshot}
                />
              ))
            )}
          </div>

          {/* Modal 分页尾部 */}
          {Math.ceil(appsTotal / appsPageSize) > 1 && (
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
              <span>
                共 {appsTotal} 条记录 (第 {appsPage} / {Math.ceil(appsTotal / appsPageSize)} 页)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={appsPage <= 1 || isAppsLoading}
                  onClick={() => loadApplications(job.id, appsPage - 1)}
                  className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-all cursor-pointer"
                >
                  上一页
                </button>
                <button
                  type="button"
                  disabled={appsPage >= Math.ceil(appsTotal / appsPageSize) || isAppsLoading}
                  onClick={() => loadApplications(job.id, appsPage + 1)}
                  className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-all cursor-pointer"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 简历快照详情 Modal */}
      <ResumeSnapshotModal
        data={selectedSnapshot}
        reviewingAppId={reviewingAppId}
        onClose={() => setSelectedSnapshot(null)}
        onReview={handleReviewApp}
      />

      {/* 确认录用弹窗 */}
      {acceptConfirmApp && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-emerald-600 text-xl"></i>
              确认录用该求职者
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              确定要录用该求职投递申请吗？录用后该申请状态将更新为已录用。
            </p>
            <div className="pt-2 pb-1 border-t border-b border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={rejectOtherApplications}
                  onChange={(e) => setRejectOtherApplications(e.target.checked)}
                  className="rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="font-medium">同时完成招聘（自动拒绝其他待处理申请并完成该岗位招聘）</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setAcceptConfirmApp(null)}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-all cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                disabled={Boolean(reviewingAppId)}
                onClick={() => handleReviewApp(acceptConfirmApp.id, true, rejectOtherApplications)}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-all cursor-pointer shadow-md shadow-emerald-500/20 disabled:opacity-50 flex items-center gap-1"
              >
                {reviewingAppId === acceptConfirmApp.id ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-check-line"></i>
                )}
                确认录用
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
